<script lang="ts" setup>
import Modal from '@/components/shared/Dialog.vue';
import DSButton from 'primevue/button';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import { useRouter } from 'vue-router';
import { RouteNames } from '@/data/routes';
import TeamCollaborationIcon from '@/assets/icons/team-collaboration.svg';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

interface Props {
  visible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const router = useRouter();

const dismissModalAndSetLocalStorage = () => {
  LocalStorage.setItem(LocalStorageKeys.DISMISSED_COLLABORATE_MODAL, true);
  emit('close');
};

const handleSkipClick = () => {
  dismissModalAndSetLocalStorage();
};

const handleInviteClick = () => {
  dismissModalAndSetLocalStorage();
  analytics.track(SEGMENT_EVENTS.if_user_invite_modal_opened, {
    source: 'collaborate_modal',
  });
  router.push({
    name: RouteNames.TEAM.INDEX,
    query: { openInviteModal: 'true' },
  });
};
</script>

<template>
  <Modal
    :visible="props.visible"
    header="Invite New Member"
    :modal="true"
    :draggable="false"
    append-to="self"
    @update:visible="!$event && handleSkipClick()"
  >
    <div :class="$style['modal-content']">
      <div :class="$style['image-container']">
        <TeamCollaborationIcon :class="$style['team-collaboration-icon']" />
      </div>
      <p :class="$style.description">
        Would you like to invite other members to collaborate on your business
        profile? You can also add them later in your <strong>Team</strong> under
        <strong>My Account</strong>.
      </p>
      <div :class="$style.buttons">
        <DSButton
          label="Skip for now"
          class="p-button-outlined"
          :class="$style['modal-button']"
          @click="handleSkipClick"
        />
        <DSButton
          label="Invite New Member"
          :class="$style['modal-button']"
          @click="handleInviteClick"
        />
      </div>
    </div>
  </Modal>
</template>

<style lang="less" module>
.modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10px;
  max-width: 685px;
  margin: 0 auto;
}

.image-container {
  margin-bottom: 20px;
  margin-top: 10px;
  display: flex;
  justify-content: center;

  .team-collaboration-icon {
    width: 120px;
    height: 120px;
  }
}

.description {
  font-size: 16px;
  line-height: 1.5;
  color: #333;
  margin-bottom: 30px;
}

.buttons {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 15px;

  .modal-button {
    flex-grow: 1;
  }
}
</style>
